import { ChangeDetectionStrategy, Component, DOCUMENT, ElementRef, effect, inject, input, output } from '@angular/core';

@Component({
    selector: 'app-confirm-dialog',
    templateUrl: './confirm-dialog.component.html',
    styleUrls: ['./confirm-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown)': 'onKeydown($event)'
    }
})
export class ConfirmDialog {
    readonly isOpen = input.required<boolean>();
    readonly message = input.required<string>();
    readonly confirmLabel = input<string>('Remove');
    readonly cancelLabel = input<string>('Cancel');

    readonly confirmed = output<void>();
    readonly cancelled = output<void>();

    private readonly _elementRef = inject(ElementRef);
    private readonly _document = inject(DOCUMENT);
    private _previousFocus: HTMLElement | null = null;

    private readonly _focusTrap = effect(() => {
        if (this.isOpen()) {
            this._previousFocus = this._document.activeElement as HTMLElement;
            // Defer focus until after the @if block has rendered
            queueMicrotask(() => {
                const focusable = this._getFocusableElements();
                focusable[0]?.focus();
            });
        } else if (this._previousFocus) {
            this._previousFocus.focus();
            this._previousFocus = null;
        }
    });

    private _getFocusableElements(): HTMLElement[] {
        const dialog = this._elementRef.nativeElement.querySelector('.confirm-dialog');
        if (!dialog) {
            return [];
        }
        return Array.from(
            dialog.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            ) as NodeListOf<HTMLElement>
        );
    }

    onConfirm(): void {
        this.confirmed.emit();
    }

    onCancel(): void {
        this.cancelled.emit();
    }

    onKeydown(event: KeyboardEvent): void {
        if (!this.isOpen()) {
            return;
        }

        if (event.key === 'Escape') {
            event.stopPropagation();
            this.cancelled.emit();
            return;
        }

        if (event.key === 'Tab') {
            const focusable = this._getFocusableElements();
            if (focusable.length === 0) {
                return;
            }
            const first = focusable[0];
            const last = focusable.at(-1);

            if (event.shiftKey && this._document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && this._document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    }
}
